import React, { useState } from "react";
import { Prompt } from "react-router";
import CalibrationApi from "../../../api/CalibrationApi";
import { EModuleType } from "../../../Model/EModuleType";
import {
  Waypoint,
  EArmType,
  EAxisType,
  ModuleWaypoint,
  toCartesianWaypoint,
  toCylindricalWaypoint,
} from "../../../Model/Waypoint";
import { getModulesByType } from "../../../redux/reducers/ModuleReducer";
import { getModuleStateById } from "../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import MtiButton from "../../Buttons/MtiButton";
import Breadcrumb from "../../Header/Breadcrumb";
import Header from "../../Header/Header";
import LayoutContent from "../../LayoutContent";
import SettingsNavSubMenu from "../SettingsNavSubMenu";
import ArmCarrierWaypointSelector from "./ArmCarrierWaypointSelector";
import { SelectionUpdatedArg } from "./ArmCarrierWaypointSelector";
import WaypointEditor from "./WaypointEditor";
import { ECarrier } from "../../../Model/ECarrier";
import { SlotSelection } from "../../../Model/StorageConfig";
import SettingTools from "./SettingTools";

const WAYPOINT_NAME_KEY = "WAYPOINT_NAME_KEY";

export default function VectorEditor() {
  const [waypoint, setWaypoint] = useState(null as Waypoint | null);
  const [originalWaypoint, setOriginalWaypoint] = useState(
    null as Waypoint | null
  );
  const [moduleIdentifier, setModuleIdentifier] = useState("");
  const [armType, setArmType] = useState(EArmType.ImagingArm);
  const [isWaypointUpdated, setIsWaypointUpdated] = useState(false);
  const [applyOffsetWhileTuning, setApplyOffsetWhileTuning] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(ECarrier.Unknown);
  const [selectedHotelNumber, setSelectedHotelNumber] = useState(0);
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(0);
  const [isError, setIsError] = useState(false);

  //hack: firefox v100 show auto hide scrollbar as default
  const [disableScrollWhenShowPopup, setDisableScrollWhenShowPopup] =
    useState(false);

  const suiteModule = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.SUITE][0]
  );
  const moduleStateById = useTypedSelector((state) =>
    getModuleStateById(state)
  );
  const suiteState = moduleStateById[suiteModule.identifier].suite;

  if (suiteState == null) {
    throw new Error("Suite state is null");
  }

  const applyChanges = () => {
    CalibrationApi.updateWaypoint(
      selectedCarrier,
      selectedHotelNumber,
      selectedSlotNumber,
      {
        moduleIdentifier: moduleIdentifier,
        cartesianWaypoints:
          waypoint?.axisType == EAxisType.Cartesian
            ? [toCartesianWaypoint(waypoint)]
            : [],
        cylindricalWaypoints:
          waypoint?.axisType == EAxisType.Cylindrical
            ? [toCylindricalWaypoint(waypoint)]
            : [],
      }
    );
    setIsWaypointUpdated(false);
  };

  const revertChanges = () => {
    const oriWaypoint = localStorage.getItem('originalWaypoint')
    if (oriWaypoint) {
      setOriginalWaypoint(JSON.parse(oriWaypoint));
      setWaypoint(JSON.parse(oriWaypoint));
      localStorage.removeItem('originalWaypoint');
    } else if (originalWaypoint != null) {
      const wp: Waypoint = JSON.parse(JSON.stringify(originalWaypoint));
      setWaypoint(wp);
    } else {
      throw Error("originai waypoint is null");
    }
    setIsWaypointUpdated(false);
  };

  const onWaypointUpdated = (waypoint: Waypoint) => {
    setWaypoint(waypoint);
    setIsWaypointUpdated(true);
  };

  const onWaypointSelectorChange = (arg: SelectionUpdatedArg) => {
    setModuleIdentifier(arg.selectedModuleIdentifier);
    setSelectedCarrier(arg.selectedCarrier);
    setArmType(arg.selectedArmType);
    setWaypoint(arg.selectedWaypoint);
    setOriginalWaypoint(arg.selectedWaypoint);
    setSelectedHotelNumber(arg.selectedHotelNumber ?? 1);
    setSelectedSlotNumber(arg.selectedSlotNumber ?? 1);
    localStorage.setItem('originalWaypoint', JSON.stringify(arg.selectedWaypoint));
  };


  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Vector Editor",
                to: "/settings/vector-editor",
                disabled: false,
              },
            ]}
          />
          <Header.RightSide>
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 4px",
              }}
              disabled={!isWaypointUpdated}
              onClick={revertChanges}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              style={{
                margin: "auto 16px",
              }}
              onClick={applyChanges}
              disabled={!isWaypointUpdated || isError}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>

      <LayoutContent.Content
        style={{
          paddingLeft: 24,
          paddingTop: 24,
          paddingBottom: 18,
          overflow: disableScrollWhenShowPopup ? "hidden" : "auto",
        }}
      >
        <ArmCarrierWaypointSelector
          onSelectionUpdated={onWaypointSelectorChange}
        />
        {waypoint && moduleIdentifier.length > 0 && (
          <WaypointEditor
            applyOffsetWhileTuning={applyOffsetWhileTuning}
            waypoint={waypoint}
            setWaypoint={onWaypointUpdated}
            moduleIdentifier={moduleIdentifier}
            armType={armType}
            isError={isError}
            setIsError={setIsError}
          />
        )}
      </LayoutContent.Content>

      <LayoutContent.CommandPanel
        style={{
          width: 600,
          boxShadow: "none",
          borderTop: "6px solid #828282",
          borderLeft: "1px solid #C2C2C2",
          overflowY: "auto",
        }}
      >
          <div
            style={{
              height: "64px",
              backgroundColor: "#414042",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              paddingLeft: "20px",
            }}
          >
            Setting Tools
          </div>
          {waypoint && moduleIdentifier.length > 0 && (
            <SettingTools
              moduleIdentifier={moduleIdentifier}
              armType={armType}
              waypoint={waypoint}
              onWaypointUpdated={onWaypointUpdated}
              applyOffsetWhileTuning={applyOffsetWhileTuning}
              setApplyOffsetWhileTuning={setApplyOffsetWhileTuning}
              selectedCarrier={selectedCarrier}
              onSelectedCarrier={setSelectedCarrier}
            />
          )}
          <Prompt
            when={isWaypointUpdated && !isError}
            message={(location) =>
              `Changes are not saved, are you sure you want to leave Vector Editor ?`
            }
          />
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}

VectorEditor.whyDidYouRender = true;
